import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { QualityBadge } from "@/components/QualityBadge";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ChangeMobileDialog, ChangeHistoryDialog } from "@/components/ControlledEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Phone, MessageCircle, UserPlus, Search, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useCallTracker } from "@/components/CallTracker";

const STATUSES = ["new", "assigned", "contacted", "interested", "follow_up", "quote_generated", "proposal", "booked", "converted", "not_interested", "not_reachable", "wrong_number", "lost"];
const SOURCES = ["website", "meta", "google", "referral", "existing_customer", "partner", "manual", "api_integration", "other"];
const TEMPS = ["hot", "warm", "cold"];

export default function Leads() {
  const { trackCall } = useCallTracker();
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [status, setStatus] = useState("all");
  const [temp, setTemp] = useState("all");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [assignTo, setAssignTo] = useState("");

  const params = { ...(q ? { q } : {}), ...(status !== "all" ? { status } : {}), ...(temp !== "all" ? { temperature: temp } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/leads", params);

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";
  const staff = users.filter((u) => ["telecaller", "team_leader", "renewal_executive", "sales_manager"].includes(u.role));

  const create = async (v) => { await api.post("/leads", v); refetch(); };
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const bulkAssign = async () => {
    if (!assignTo || selected.length === 0) { toast.error("Select leads and an employee"); return; }
    await api.post("/leads/bulk-assign", { ids: selected, assigned_to: assignTo });
    toast.success(`${selected.length} leads reassigned`);
    setSelected([]); setAssignTo(""); refetch();
  };

  const convert = async (lead) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`);
      toast.success("Lead converted to customer");
      nav(`/customers/${data.id || data.customer_id}`);
    } catch (e) { toast.error("Convert failed"); }
  };

  const fields = [
    { name: "name", label: "Customer Name", required: true },
    { name: "mobile", label: "Primary Mobile", required: true },
    { name: "alternate_mobile", label: "Alternate Mobile" },
    { name: "email", label: "Email", type: "email" },
    { name: "city", label: "City" }, { name: "state", label: "State" }, { name: "pincode", label: "PIN Code" },
    { name: "insurance_type", label: "Insurance Type", type: "select", options: ["Car Insurance", "Bike Insurance", "Health Insurance", "Life Insurance", "Travel Insurance"] },
    { name: "source", label: "Lead Source", type: "select", options: SOURCES.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "temperature", label: "Temperature", type: "select", options: TEMPS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "priority", label: "Priority", type: "select", options: ["high", "medium", "low"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "assigned_to", label: "Assign To (blank = auto by area rule)", type: "select", options: staff.map((u) => ({ value: u.id, label: `${u.name} (${titleCase(u.role)})` })) },
    { name: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  const columns = [
    ...(hasPerm("leads:assign") ? [{ key: "sel", header: "", tdClass: "w-10", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid="lead-select-checkbox" /></div>
    ) }] : []),
    { key: "lead_id", header: "Lead ID", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.lead_id}</span> },
    { key: "name", header: "Name", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-xs text-muted-foreground">{r.mobile}</div></div> },
    { key: "insurance_type", header: "Type", render: (r) => <span className="text-sm">{r.insurance_type || "—"}</span> },
    { key: "source", header: "Source", render: (r) => <span className="text-sm">{titleCase(r.source)}</span> },
    { key: "temperature", header: "Temp", render: (r) => <StatusBadge value={r.temperature} /> },
    { key: "quality", header: "Quality", render: (r) => <QualityBadge quality={r.quality} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "assigned_to", header: "Assigned", render: (r) => <span className="text-sm">{userName(r.assigned_to)}</span> },
    { key: "actions", header: "Actions", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" asChild data-testid="lead-action-call-button"><a href={`tel:${r.mobile}`} onClick={() => trackCall({ business: "insurance", lead_id: r.id, name: r.name, mobile: r.mobile })}><Phone className="h-4 w-4" /></a></Button>
        <WhatsAppButton lead={r} />
        {hasPerm("customers:add") && r.status !== "converted" && (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => convert(r)} title="Convert to customer" data-testid="lead-convert-button"><UserPlus className="h-4 w-4" /></Button>
        )}
        {hasPerm("leads:edit_mobile") && <ChangeMobileDialog lead={r} leadType="insurance" onDone={refetch} />}
        {(hasPerm("audit:view") || hasPerm("leads:edit_mobile")) && <ChangeHistoryDialog entityId={r.id} />}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Lead Management" subtitle={`${total} leads in pipeline`} icon={Users}
        actions={hasPerm("leads:add") && (
          <FormDialog title="New Lead" fields={fields} onSubmit={create} submitLabel="Create Lead" testId="create-lead-dialog"
            trigger={<Button data-testid="create-lead-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Lead</Button>} />
        )} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, mobile, lead ID..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="lead-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
        <Select value={temp} onValueChange={setTemp}><SelectTrigger className="w-36"><SelectValue placeholder="Temp" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Temps</SelectItem>{TEMPS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>

      {hasPerm("leads:assign") && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <ArrowRightLeft className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{selected.length} selected</span>
          <Select value={assignTo} onValueChange={setAssignTo}><SelectTrigger className="w-56 ml-auto" data-testid="bulk-assign-select"><SelectValue placeholder="Assign to employee" /></SelectTrigger>
            <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
          <Button onClick={bulkAssign} data-testid="bulk-assign-btn">Reassign</Button>
        </div>
      )}

      <DataTable columns={columns} rows={items} loading={loading} rowTestId="lead-management-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
